// src/components/ReferralLanding.js
//
// Landing for the /r/:code referral QR route. On mount it records the scan via
// the record-referral-scan EF (verify_jwt:false — we're an unauthenticated
// visitor by intent; the supabase client attaches the anon key, no auth header),
// then routes the visitor to signup with the referrer's label as social proof.
//
// Attribution rides on the EXISTING Auth.js plumbing: we stash the same
// `casavio_ref` localStorage key Auth already reads (-> profiles.referred_by),
// plus `casavio_ref_label` for the "Invited by ..." header (Auth renders it, C3).
// The only writes here are the EF's scan tracking — no account-data writes.

import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabase'
import Auth from './Auth'
import InstallPrompt from './InstallPrompt'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', brown: '#2C1D14', muted: '#9A7B68', danger: '#B03020',
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

const btn = {
  background: C.amber, color: '#fff', border: 'none', borderRadius: 10,
  padding: '11px 20px', fontSize: 15, fontFamily: f, fontWeight: 700, cursor: 'pointer', minHeight: 44,
}
const btnGhost = {
  background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 10,
  padding: '11px 20px', fontSize: 14, fontFamily: fs, cursor: 'pointer', minHeight: 44,
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '32px 28px', maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 8px 32px rgba(44,29,20,0.12)' }}>
        {children}
      </div>
    </div>
  )
}

export default function ReferralLanding({ code, session }) {
  const [status, setStatus] = useState('loading') // loading | ok | inactive | unknown | error
  const [label, setLabel] = useState(null)
  const scanned = useRef(false) // guard against StrictMode double-mount / re-render re-scans

  const runScan = () => {
    setStatus('loading')
    // No Authorization header — verify_jwt is false; we're an unauthenticated
    // visitor by intent. The client still attaches the anon apikey.
    supabase.functions.invoke('record-referral-scan', { body: { code, event: 'scan' } })
      .then(({ data, error }) => {
        if (error) { setStatus('error'); return }
        if (data?.ok && data.active) {
          // Stash for signup attribution — same key Auth.js already consumes.
          try {
            localStorage.setItem('casavio_ref', code)
            if (data.label) localStorage.setItem('casavio_ref_label', data.label)
          } catch { /* private mode / storage off — signup still works, just no attribution */ }
          setLabel(data.label || null)
          setStatus('ok')
        } else if (data?.reason === 'inactive') {
          setLabel(data.label || null)
          setStatus('inactive')
        } else if (data?.reason === 'unknown') {
          setStatus('unknown')
        } else {
          setStatus('error')
        }
      })
      .catch(() => setStatus('error'))
  }

  useEffect(() => {
    if (scanned.current) return
    scanned.current = true
    runScan()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (status === 'loading') {
    return <Shell><div style={{ fontFamily: fs, fontSize: 15, color: C.muted }}>Checking your invitation&hellip;</div></Shell>
  }

  if (status === 'error') {
    return (
      <Shell>
        <div style={{ fontFamily: f, fontSize: 20, color: C.brown, marginBottom: 10 }}>Something went wrong</div>
        <div style={{ fontFamily: fs, fontSize: 14, color: C.muted, marginBottom: 20 }}>We couldn’t check your invitation just now.</div>
        <button onClick={runScan} style={btn}>Retry</button>
      </Shell>
    )
  }

  if (status === 'unknown') {
    return (
      <Shell>
        <div style={{ fontFamily: f, fontSize: 20, color: C.brown, marginBottom: 10 }}>Invitation not found</div>
        <div style={{ fontFamily: fs, fontSize: 14, color: C.muted, marginBottom: 20 }}>This invitation link is expired or invalid.</div>
        <a href="/join" style={{ ...btn, textDecoration: 'none', display: 'inline-block' }}>Go to sign up</a>
      </Shell>
    )
  }

  if (status === 'inactive') {
    return (
      <Shell>
        <div style={{ fontFamily: f, fontSize: 20, color: C.brown, marginBottom: 10 }}>This invitation is no longer active</div>
        <div style={{ fontFamily: fs, fontSize: 14, color: C.muted, marginBottom: 20, lineHeight: 1.5 }}>
          {label ? `${label}’s invitation link has been turned off.` : 'This invitation link has been turned off.'} You can still sign up on your own.
        </div>
        <a href="/join" style={{ ...btn, textDecoration: 'none', display: 'inline-block' }}>Go to sign up</a>
      </Shell>
    )
  }

  // status === 'ok'
  if (session?.user) {
    return (
      <Shell>
        <div style={{ fontFamily: f, fontSize: 19, color: C.brown, marginBottom: 10 }}>You’re already signed in</div>
        <div style={{ fontFamily: fs, fontSize: 14, color: C.muted, marginBottom: 22, lineHeight: 1.5 }}>
          You’re signed in as <strong style={{ color: C.brown }}>{session.user.email}</strong>. Sign out to use this invitation link?
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Sign out → App's onAuthStateChange clears session → this component
              re-renders into the signup branch below (currentPage stays 'referral'). */}
          <button onClick={() => { supabase.auth.signOut() }} style={btn}>Sign out and continue</button>
          {/* Stay signed in → dashboard; leave localStorage stashed so a later
              sign-out + revisit still attributes. */}
          <button onClick={() => { window.location.href = '/' }} style={btnGhost}>Stay signed in</button>
        </div>
      </Shell>
    )
  }

  // Unauthenticated → signup. Auth (C3) reads casavio_ref_label for the
  // "Invited by [label]" header and honors initialMode='signup'.
  return <><Auth initialMode="signup" /><InstallPrompt /></>
}
